import DataTable from "react-data-table-component";
import { getAllSales } from "../../api/salesApi";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from 'xlsx';
import { FileDown, Eye, Search, Filter } from 'lucide-react';
import SalesInvoice from '../Invoice/SalesInvoice'; // Import the invoice component
import { FaWhatsapp } from "react-icons/fa";

const MySalesData = ({
  refreshKey,
  startDate = null,
  endDate = null,
  branch_id,
  admin_id,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterPaymentType, setFilterPaymentType] = useState("");
  const [selectedSale, setSelectedSale] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const { adminSelectedBranch, adminData } = useAuth();

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const today = new Date();
        const todayStart =
          startDate ||
          `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
        const todayEnd =
          endDate ||
          `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
        const params = {
          startDate: todayStart,
          endDate: todayEnd,
          admin_id: admin_id || adminData.admin_id,
        };

        if (
          (branch_id !== null && branch_id !== undefined) ||
          (adminSelectedBranch !== null &&
            adminSelectedBranch !== undefined &&
            adminSelectedBranch !== "")
        ) {
          params.branch_id =
            branch_id !== null && branch_id !== undefined
              ? branch_id
              : adminSelectedBranch.branch_id;
        }

        const sales = await getAllSales(params);
        setData(sales);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [refreshKey, adminSelectedBranch, adminData, startDate, endDate]);

  // Enhanced columns with more information
  const columns = [
    { 
      name: "Invoice ID", 
      selector: (row) => row.sales_id, 
      sortable: true,
      width: "150px",
      cell: (row) => <span className="font-mono text-sm">{row.sales_id}</span>
    },
    {
      name: "Date & Time",
      selector: (row) => row.createdAt,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="text-sm">
          <div>{new Date(row.createdAt).toLocaleDateString()}</div>
          <div className="text-gray-500 text-xs">
            {new Date(row.createdAt).toLocaleTimeString()}
          </div>
        </div>
      )
    },
    {
      name: "Client Details",
      selector: (row) => row.client.username,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div className="text-sm">
          <div className="font-semibold">{row.client.username}</div>
          <div className="text-gray-500 text-xs">{row.client.mobile}</div>
        </div>
      )
    },
    {
      name: "Vehicle Info",
      selector: (row) => row.vehicle?.model || "N/A",
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="text-sm">
          <div className="font-semibold">{row.vehicle?.model || "N/A"}</div>
          <div className="text-gray-500 text-xs">{row.vehicle?.plate_number || "N/A"}</div>
        </div>
      )
    },
    {
      name: "Action Type",
      selector: (row) => row.action,
      sortable: true,
      width: "120px",
      cell: (row) => {
        const colors = {
          SUBSCRIBE: "bg-green-100 text-green-800",
          RENEW: "bg-blue-100 text-blue-800",
          WASH: "bg-purple-100 text-purple-800",
          "FREE WASH": "bg-orange-100 text-orange-800"
        };
        return (
          <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[row.action] || "bg-gray-100 text-gray-800"}`}>
            {row.action}
          </span>
        );
      }
    },
    {
      name: "Payment",
      selector: (row) => row.payment_type,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          row.payment_type === "CARD" ? "bg-blue-50 text-blue-700" : 
          row.payment_type === "CASH" ? "bg-green-50 text-green-700" : 
          row.payment_type === "ONLINE" ? "bg-purple-50 text-purple-700" : 
          "bg-yellow-50 text-yellow-700"
        }`}>
          {row.payment_type}
        </span>
      )
    },
    { 
      name: "Branch & Admin", 
      selector: (row) => row?.branch?.name, 
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div className="text-sm">
          <div className="font-semibold">{row?.branch?.name || "N/A"}</div>
          <div className="text-gray-500 text-xs">{row.admin?.username || "N/A"}</div>
        </div>
      )
    },
    {
      name: "Price",
      selector: (row) => row.price,
      sortable: true,
      width: "100px",
      cell: (row) => <span className="text-sm">QAR {row.price.toFixed(2)}</span>
    },
    {
      name: "Discount",
      selector: (row) => row.discount,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span className="text-sm text-red-600">
          {row.discount > 0 ? `-QAR ${row.discount.toFixed(2)}` : "-"}
        </span>
      )
    },
    { 
      name: "Total", 
      selector: (row) => row.total, 
      sortable: true,
      width: "120px",
      cell: (row) => <span className="text-sm font-bold text-green-700">QAR {row.total.toFixed(2)}</span>
    },
    {
      name: "Actions",
      width: "150px",
      cell: (row) => (
        <div className="flex">
          <button
            onClick={() => setSelectedSale(row)}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            <Eye size={14} />
          </button>
          <a href={`https://wa.me/974${row.client.mobile}?text=Hello%20${encodeURIComponent(row.client.username)},%20regarding%20your%20invoice%20ID%20${row.sales_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm ms-2">
            <FaWhatsapp/>
          </a>    
        </div>
      )
    }
  ];

  const summary = useMemo(
    () => ({
      totalRecords: data ? data.length : 0,
      totalSales: data ? data.reduce((sum, item) => sum + item.total, 0) : 0,
      totalDiscount: data ? data.reduce((sum, item) => sum + item.discount, 0) : 0,
      averageSale:
        data && data.length
          ? data.reduce((sum, item) => sum + item.total, 0) / data.length
          : 0,
      freeWashes: data
        ? data.filter((item) => item.payment_type === "FREE WASH").length
        : 0,
      subscriptions: data
        ? data.filter((item) => item.action === "SUBSCRIBE").length
        : 0,
      renewals: data
        ? data.filter((item) => item.action === "RENEW").length
        : 0,
      washes: data
        ? data.filter((item) => item.action === "WASH").length
        : 0,
    }),
    [data]
  );

  // Enhanced filtering
  const filteredData = data.filter((item) => {
    const matchesGeneral = 
      item.sales_id.toString().toLowerCase().includes(filterText.toLowerCase()) ||
      item.client.username.toLowerCase().includes(filterText.toLowerCase()) ||
      item.client.mobile.toLowerCase().includes(filterText.toLowerCase()) ||
      (item.branch?.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.vehicle?.model || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.admin?.username || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.vehicle?.plate_number || "").toLowerCase().includes(filterText.toLowerCase());
    
    const matchesClient = filterClient === "" || 
      item.client.username.toLowerCase().includes(filterClient.toLowerCase());
    
    const matchesMobile = filterMobile === "" || 
      item.client.mobile.includes(filterMobile);
    
    const matchesAction = filterAction === "" || item.action === filterAction;
    
    const matchesPayment = filterPaymentType === "" || 
      item.payment_type === filterPaymentType;

    return matchesGeneral && matchesClient && matchesMobile && 
           matchesAction && matchesPayment;
  });

  // Export to Excel function
  const exportToExcel = () => {
    const exportData = filteredData.map(item => ({
      "Invoice ID": item.sales_id,
      "Date": new Date(item.createdAt).toLocaleDateString(),
      "Time": new Date(item.createdAt).toLocaleTimeString(),
      "Client Name": item.client.username,
      "Client Mobile": item.client.mobile,
      "Client Email": item.client.email,
      "Vehicle Model": item.vehicle?.model || "N/A",
      "Plate Number": item.vehicle?.plate_number || "N/A",
      "Vehicle Year": item.vehicle?.year_made || "N/A",
      "Action": item.action,
      "Payment Type": item.payment_type,
      "Branch": item?.branch?.name || "N/A",
      "Price (QAR)": item.price,
      "Discount (QAR)": item.discount,
      "Total (QAR)": item.total,
      "Admin": item.admin?.username || "N/A"
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Report");
    
    // Auto-size columns
    const maxWidth = exportData.reduce((w, r) => Math.max(w, r["Client Name"]?.length || 0), 10);
    ws['!cols'] = [
      { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: maxWidth }, 
      { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 25 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }
    ];
    
    const fileName = `Sales_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.action === "SUBSCRIBE",
      style: {
        backgroundColor: "rgba(63, 195, 128, 0.1)",
        "&:hover": {
          backgroundColor: "rgba(63, 195, 128, 0.2)",
          cursor: "pointer",
        },
      },
    },
    {
      when: (row) => row.action === "RENEW",
      style: {
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        "&:hover": {
          backgroundColor: "rgba(59, 130, 246, 0.2)",
          cursor: "pointer",
        },
      },
    },
    {
      when: (row) => row.action === "WASH",
      style: {
        backgroundColor: "rgba(168, 85, 247, 0.1)",
        "&:hover": {
          backgroundColor: "rgba(168, 85, 247, 0.2)",
          cursor: "pointer",
        },
      },
    },
  ];

  return (
    <div className="text-black flex justify-center row w-full">
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-semibold">Error loading data:</p>
          <p>{error}</p>
        </div>
      ) : (
        <div className="w-full mt-2">
          {/* Enhanced Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-600">
              <p className="text-xs text-gray-600">Total Records</p>
              <p className="text-xl font-bold text-blue-600">{summary.totalRecords}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-600">
              <p className="text-xs text-gray-600">Total Sales</p>
              <p className="text-xl font-bold text-green-600">
                QAR {summary.totalSales.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-600">
              <p className="text-xs text-gray-600">Avg Sale</p>
              <p className="text-xl font-bold text-yellow-600">
                QAR {summary.averageSale.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border-l-4 border-red-600">
              <p className="text-xs text-gray-600">Total Discount</p>
              <p className="text-xl font-bold text-red-600">
                QAR {summary.totalDiscount.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border-l-4 border-purple-600">
              <p className="text-xs text-gray-600">Free Washes</p>
              <p className="text-xl font-bold text-purple-600">{summary.freeWashes}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg border-l-4 border-green-700">
              <p className="text-xs text-gray-600">Subscriptions</p>
              <p className="text-xl font-bold text-green-700">{summary.subscriptions}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg border-l-4 border-blue-700">
              <p className="text-xs text-gray-600">Renewals</p>
              <p className="text-xl font-bold text-blue-700">{summary.renewals}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg border-l-4 border-purple-700">
              <p className="text-xs text-gray-600">Washes</p>
              <p className="text-xl font-bold text-purple-700">{summary.washes}</p>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="flex flex-col md:flex-row gap-3 mb-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by Invoice ID, Client, Branch, Vehicle..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                    focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
                />
                <div className="absolute inset-y-0 start-0 flex items-center ps-3">
                  <Search className="w-4 h-4 text-gray-500" />
                </div>
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 
                  rounded-lg transition-colors"
              >
                <Filter size={18} />
                {showFilters ? "Hide" : "Show"} Filters
              </button>

              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white 
                  rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileDown size={18} />
                Export to Excel
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-3 border-t">
                <input
                  type="text"
                  placeholder="Filter by Client Name"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Filter by Mobile Number"
                  value={filterMobile}
                  onChange={(e) => setFilterMobile(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm"
                />
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm"
                >
                  <option value="">All Actions</option>
                  <option value="SUBSCRIBE">Subscribe</option>
                  <option value="RENEW">Renew</option>
                  <option value="WASH">Wash</option>
                </select>
                <select
                  value={filterPaymentType}
                  onChange={(e) => setFilterPaymentType(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm"
                >
                  <option value="">All Payment Types</option>
                  <option value="CARD">Card</option>
                  <option value="CASH">Cash</option>
                  <option value="ONLINE">Online</option>
                  <option value="FREE WASH">Free Wash</option>
                </select>
              </div>
            )}

            <div className="mt-3 text-sm text-gray-600">
              Showing {filteredData.length} of {data.length} records
            </div>
          </div>

          {/* Data Table */}
          <div className="data-table overflow-x-auto bg-white rounded-lg shadow">
            <DataTable
              title="SALES REPORT"
              data={filteredData}
              columns={columns}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              fixedHeader={true}
              fixedHeaderScrollHeight="500px"
              conditionalRowStyles={conditionalRowStyles}
              highlightOnHover
              pointerOnHover
              dense
            />
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {selectedSale && (
        <SalesInvoice
          sale={selectedSale}
          onClose={() => setSelectedSale(null)}
        />
      )}
    </div>
  );
};

export default MySalesData;

//active subscriptions total